// LT-314 — DS v1 empty-state card. Replaces the "bare empty table" pattern
// across primary list pages (Sequences, Contacts, Calls, Deals, Accounts,
// FollowUps). One component, passes the AC-required shape:
//   - icon (lucide or heroicon component)
//   - headline
//   - 1-sentence subhead
//   - primary CTA (required)
//   - secondary CTA (optional — e.g. "Start from template")
//
// Analytics: clicking a CTA fires `empty_state.cta_clicked` with
// `{ surface, cta }` so we can slice per-page conversion without adding
// per-page event names.

import React from 'react';
import { Link } from 'react-router-dom';
import { track } from '../utils/analytics';
import { AnalyticsEvents } from '../utils/analyticsEvents';
import { Button } from './Button';

export interface EmptyStateCta {
  /** Label shown on the button. */
  label: string;
  /**
   * When `to` is set, renders a react-router `<Link>` wrapped in a ghost
   * `<span>` styled via Button classes. When `onClick` is set, renders a
   * `<Button>` that fires `onClick`. Callers supply one or the other —
   * not both — and the component picks the right element.
   */
  to?: string;
  onClick?: () => void;
  /** Optional lucide/heroicon rendered before the label. */
  icon?: React.ComponentType<{ className?: string }>;
}

export interface EmptyStateProps {
  /**
   * Short string identifying the calling surface for analytics
   * (`empty_state.cta_clicked` event `surface` prop). Examples:
   * 'sequences_list', 'contacts_list'.
   */
  surface: string;
  icon: React.ComponentType<{ className?: string }>;
  headline: string;
  subhead: string;
  primary: EmptyStateCta;
  secondary?: EmptyStateCta;
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  surface,
  icon: Icon,
  headline,
  subhead,
  primary,
  secondary,
  className,
}) => {
  const fire = (cta: EmptyStateCta, kind: 'primary' | 'secondary') => {
    track(AnalyticsEvents.EMPTY_STATE_CTA_CLICKED, {
      surface,
      cta: cta.label,
      kind,
    });
    cta.onClick?.();
  };

  return (
    <div
      className={`bg-surface-1 border border-white/[0.08] rounded-ds-xl px-6 py-10 flex flex-col items-center text-center ${
        className ?? ''
      }`}
      data-testid="empty-state"
      data-surface={surface}
    >
      <div className="p-2 rounded-ds-lg bg-accent-tint border border-accent-border mb-3">
        <Icon className="w-5 h-5 text-accent" />
      </div>
      <h3 className="text-base font-semibold text-slate-50 mb-1">{headline}</h3>
      <p className="text-sm text-slate-400 max-w-md mb-5">{subhead}</p>
      <div className="flex items-center gap-2">
        {primary.to ? (
          <Link
            to={primary.to}
            onClick={() => fire(primary, 'primary')}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-ds-md text-sm font-medium bg-accent hover:bg-accent-hover text-white shadow-ds-glow-accent transition-colors"
          >
            {primary.icon ? <primary.icon className="w-4 h-4" /> : null}
            <span>{primary.label}</span>
          </Link>
        ) : (
          <Button intent="primary" size="sm" onClick={() => fire(primary, 'primary')}>
            {primary.icon ? <primary.icon className="w-4 h-4" /> : null}
            <span>{primary.label}</span>
          </Button>
        )}
        {secondary ? (
          secondary.to ? (
            <Link
              to={secondary.to}
              onClick={() => fire(secondary, 'secondary')}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-ds-md text-sm font-medium border border-white/[0.08] text-slate-200 hover:bg-surface-2 transition-colors"
            >
              {secondary.icon ? <secondary.icon className="w-4 h-4" /> : null}
              <span>{secondary.label}</span>
            </Link>
          ) : (
            <Button intent="secondary" size="sm" onClick={() => fire(secondary, 'secondary')}>
              {secondary.icon ? <secondary.icon className="w-4 h-4" /> : null}
              <span>{secondary.label}</span>
            </Button>
          )
        ) : null}
      </div>
    </div>
  );
};

export default EmptyState;
